import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError, tap, switchMap, shareReplay } from 'rxjs/operators';
import { AddressLine, MeterDetails, MeterType, FuelType, Meter, ConsentStatus, AddressMasterSearchTemplate, ElecMeterDetailsTemplate, GasMeterDetailsTemplate, ElecMeterDetails, GasMeterDetails } from '@models/meter.model';
import { HttpClient, HttpParams } from '@angular/common/http';
import { MasterSearchService } from './master-search.service';
import { addressMastertoAddress, elecMeterMasterSearchToMeterDetails, gasMeterMasterSearchToMeterDetails, mapRelationshipTypeFromApi, mapRelationshipTypeToApi } from '@utils/meter.utils';
import { ChatAgentResponse } from '@models/master-search.models';
import { LoginService } from './login.service';
import { ConsentService } from './consent.service';
import { Consent, ConsentType, DigitalSignature, RelationshipType, StripeCredentials } from '@models/consent.model';
import { calculateConsentDateRange, formatDateForApi } from '@utils/meter.utils';
import { SetupIntent, Stripe, StripeElements } from '@stripe/stripe-js';

interface ApprovedMetersApiResponse {
  status: boolean;
  code: string;
  timestamp: string;
  message: {
    email: string;
    totalApprovedMeters: number;
    approvedMeters: Array<{
      consentId: string;
      meter: string;
      fuelType: string;
      meterType: string;
      consentStartDate: string;
      consentEndDate: string;
      scope: string;
      relationshipType?: string; 
      siteAddress: string;
      totalEac?: number; // Optional, may not be present in all responses
    }>;
  };
  errors: any[];
  additionalInfo: Record<string, any>;
}

@Injectable({
  providedIn: 'root'
})
export class MeterService {
  private baseUrlCarbon = `${environment.baseUrlCarbon}/carbon`;

  private readonly http = inject(HttpClient);
  private readonly masterSearch = inject(MasterSearchService);
  private readonly loginService = inject(LoginService);
  private readonly consentService = inject(ConsentService);

  private endPointURL = `${environment.baseUrl}/core`;
  
  // Cache for meters data
  private metersCache$: Observable<Meter[]> | null = null;
  private cacheTimestamp: number = 0;
  private readonly cacheExpiryMs = 5 * 60 * 1000; // 5 minutes cache expiry
  
  constructor() { 
    console.log('MeterService initialized');
  }

  /**
   * Get all meters as an observable
   * @param forceLoad - If true, forces a fresh API call bypassing cache. Defaults to true.
   * @returns Observable<Meter[]>
   */
  getMeters(forceLoad: boolean = true): Observable<Meter[]> {
    // Check if we should use cache
    if (!forceLoad && this.metersCache$ && this.isCacheValid()) {
      console.log('Returning meters from cache');
      return this.metersCache$;
    }

    console.log('Fetching meters from API');
    // Create fresh observable and cache it
    this.metersCache$ = this.fetchMetersFromApi().pipe(
      switchMap((meters) => {
        // After fetching meters, also get sharee information and counts to enrich the data
        return this.consentService.getActiveShareeNamesByMeter().pipe(
          map((shareInfoByMeter) => {
            // Add sharee information and counts to meters
            return meters.map(meter => {
              const shareInfo = shareInfoByMeter.get(meter.meterNumber) || [];
              return { 
                ...meter, 
                numberOfShares: shareInfo.length,
                shareeNames: shareInfo
              };
            });
          }),
          catchError(() => {
            // If getting shares fails, just return the meters without share data
            console.warn('Could not get sharee information, returning meters without share data');
            return of(meters);
          })
        );
      }),
      shareReplay(1), // Share the result with multiple subscribers
      tap(() => {
        // Update cache timestamp when data is successfully fetched
        this.cacheTimestamp = Date.now();
      })
    );

    return this.metersCache$;
  }

  /**
   * Check if the current cache is still valid
   * @returns boolean indicating if cache is valid
   */
  private isCacheValid(): boolean {
    return Date.now() - this.cacheTimestamp < this.cacheExpiryMs;
  }

  /**
   * Clear the meters cache
   */
  private clearCache(): void {
    this.metersCache$ = null;
    this.cacheTimestamp = 0;
  }
  
  /**
   * Fetch meters from the API
   * @returns Observable<Meter[]>
   */
  private fetchMetersFromApi(): Observable<Meter[]> {
    const url = `${this.endPointURL}/v1/customer/approved-meters`;

    return this.loginService.loginUserEmail.pipe(
      switchMap(email => {
        if (!email) {
          console.error('User email not available. User may not be logged in.');
          return of([]);
        }
        return this.http.post<ApprovedMetersApiResponse>(url, { email }, {
          headers: {
            'Content-Type': 'application/json'
          },
          params: {
            page: '1',
            limit: '5000'
          }
        }).pipe(
          map(response => {
            if (response.status && response.message?.approvedMeters) {
              return response.message.approvedMeters.map(meterData => {
                // Convert API meter data to our Meter model
                const now = new Date();
                const startDate = new Date(meterData.consentStartDate);
                const endDate = new Date(meterData.consentEndDate);
                
                // Determine consent status
                const consentStatus = (now <= endDate) 
                  ? ConsentStatus.ACTIVE 
                  : ConsentStatus.EXPIRED;
                
                // Map fuel type from API to our enum
                const fuelType = meterData.fuelType;
                
                // Map meter type from API to our enum
                let meterType = MeterType.NON_HALF_HOURLY;
                if (meterData.meterType === 'HH') {
                  meterType = MeterType.HALF_HOURLY;
                } else if (meterData.meterType === 'SMART') {
                  meterType = MeterType.SMART;
                }
                
                return {
                  meterNumber: meterData.meter,
                  meterType: meterType,
                  fuelType: fuelType,
                  address: meterData.siteAddress,
                  postCode: '',
                  consentStatus: consentStatus,
                  relationship: mapRelationshipTypeFromApi(meterData.relationshipType), // Falls back to landlord if missing or unknown
                  startDate: startDate, // Added start date
                  expiryDate: endDate,
                  numberOfShares: 0, // Initialize to 0, will be updated by getMeters
                  consentId: meterData.consentId,
                  consumption: meterData.totalEac || null
                } as Meter;
              });
            }
            return [];
          }),
          catchError(error => {
            console.error('Error fetching meters:', error);
            return of([]);
          })
        );
      }),
      shareReplay(1)
    );
  }

  /**
   * Refresh meters from the API
   * @returns Observable<Meter[]>
   */
  refreshMeters(): Observable<Meter[]> {
    // Clear cache and force fresh load
    this.clearCache();
    return this.getMeters(true);
  }

  /**
   * Find a meter by meter number
   * @param meterNumber The meter number to search for
   * @param forceLoad Whether to force load from API (defaults to false for this method)
   * @returns Observable<Meter | undefined>
   */
  findMeterByNumber(meterNumber: string, forceLoad: boolean = false): Observable<Meter | undefined> {
    return this.getMeters(forceLoad).pipe(
      map(meters => meters.find(m => m.meterNumber === meterNumber))
    );
  }

  /**
   * Get meters with share counts 
   * @param forceLoad Whether to force load from API (defaults to false for this method)
   * @returns Observable<Meter[]>
   */
  getMetersWithShares(forceLoad: boolean = false): Observable<Meter[]> {
    return this.getMeters(forceLoad).pipe(
      map(meters => meters.filter(meter => (meter.numberOfShares || 0) > 0))
    );
  }

  getAddressLine(postCode: string): Observable<AddressLine[]> {
    // Use the actual masterSearch API call
    return this.masterSearch.masterSearchData<AddressMasterSearchTemplate>(postCode, 'addressSearchV3', 'postcode')
    .pipe(
      map(response => response.data.addressSearchV3),
      map((addressSearch) => addressSearch.map(addressMaster => addressMastertoAddress(addressMaster)))
    );
  }

  getMeterDetailsByAddressId(addressId: string, fuelType: FuelType): Observable<MeterDetails[]> {
    // Use the actual masterSearch API call
    let meterDetailsObservable: Observable<ChatAgentResponse<ElecMeterDetailsTemplate | GasMeterDetailsTemplate>>;
    if (fuelType === FuelType.ELEC) {
      meterDetailsObservable = this.masterSearch.masterSearchData<ElecMeterDetailsTemplate>(addressId, 'elecAdvancedMeterDetails', 'pafId');
    } else {
      meterDetailsObservable = this.masterSearch.masterSearchData<GasMeterDetailsTemplate>(addressId, 'gasAdvancedMeterDetails', 'pafId');
    }

    return meterDetailsObservable.pipe(
      map(response => {
        const meterDetails = fuelType === FuelType.ELEC ? ((response.data as ElecMeterDetailsTemplate).elecAdvancedMeterDetails) : (response.data as GasMeterDetailsTemplate).gasAdvancedMeterDetails;
        return meterDetails.map(meter => {
          if (fuelType === FuelType.ELEC) {
            return elecMeterMasterSearchToMeterDetails(meter as ElecMeterDetails);
          } else {
            return gasMeterMasterSearchToMeterDetails(meter as GasMeterDetails);
          }
        });
      })
    );
  }

  getMeterDetailsByMeterNo(meterNo: string, fuelType: FuelType): Observable<MeterDetails[]> {
    // Use the actual masterSearch API call
    let meterDetailsObservable: Observable<ChatAgentResponse<ElecMeterDetailsTemplate | GasMeterDetailsTemplate>>;
    if (fuelType === FuelType.ELEC) {
      meterDetailsObservable = this.masterSearch.masterSearchData<ElecMeterDetailsTemplate>(meterNo, 'elecAdvancedMeterDetails', 'mpan');
    } else {
      meterDetailsObservable = this.masterSearch.masterSearchData<GasMeterDetailsTemplate>(meterNo, 'gasAdvancedMeterDetails', 'mprn');
    }

    return meterDetailsObservable.pipe(
      map(response => {
        const meterDetails = fuelType === FuelType.ELEC ? ((response.data as ElecMeterDetailsTemplate).elecAdvancedMeterDetails) : (response.data as GasMeterDetailsTemplate).gasAdvancedMeterDetails;
        return meterDetails.map(meter => {
          if (fuelType === FuelType.ELEC) {
            return elecMeterMasterSearchToMeterDetails(meter as ElecMeterDetails);
          } else {
            return gasMeterMasterSearchToMeterDetails(meter as GasMeterDetails);
          }
        });
      })
    );
  }

  /**
   * Add new meters by uploading a file with metadata
   * @param consent The standardized consent object containing all consent data
   * @param meters Array of meter details
   * @param email User's email
   * @param relationshipType User's relationship to the property
   * @param organisation Optional organisation details if applicable
   * @param consentStartDate Start date for the consent
   * @param consentEndDate End date for the consent
   * @returns Observable with the API response
   * 
   * Note: This method now handles all consent types including Stripe payment processing
   * for credit card consents, removing the need for complex logic in the UI components.
   */
  addNewMeter(
    consent: Consent,
    meters: MeterDetails[],
    email: string,
    relationshipType: RelationshipType,
    organisation?: { name: string; role: string; organisationNumber: string; organisationType: string },
    consentStartDate?: Date,
    consentEndDate?: Date
  ): Observable<any> {
    // Convert to async operation for Stripe processing
    return new Observable(observer => {
      this.processAddNewMeter(consent, meters, email, relationshipType, organisation, consentStartDate, consentEndDate)
        .then(result => {
          // Clear cache since new meters have been added
          this.clearCache();
          observer.next(result);
          observer.complete();
        })
        .catch(error => {
          observer.error(error);
        });
    });
  }

  private async processAddNewMeter(
    consent: Consent,
    meters: MeterDetails[],
    email: string,
    relationshipType: RelationshipType,
    organisation?: { name: string; role: string; organisationNumber: string; organisationType: string },
    consentStartDate?: Date,
    consentEndDate?: Date
  ): Promise<any> {
    const URL = `${environment.baseUrl}/core-bin/v1/customer/submit-consent`;

    // Create form data
    const formData = new FormData();
    let consentId = undefined;


    // Prepare consent dates
    const startDate = consentStartDate ? formatDateForApi(consentStartDate) : formatDateForApi(new Date());
    const endDate = consentEndDate ? formatDateForApi(consentEndDate) : formatDateForApi(new Date(new Date().setFullYear(new Date().getFullYear() + 1)));

    // Convert meters array to API format
    const metersForApi = meters.map(meter => {
      let meterTypeForApi = meter.meterType === MeterType.HALF_HOURLY ? 'HH' : (meter.meterType === MeterType.SMART ? 'SMART' : 'NHH');
      const fuelTypeForApi = meter.fuelType === FuelType.ELEC ? 'Electricity' : 'Gas';

      if (meterTypeForApi === 'SMART' && (consent.consentType === ConsentType.CHECKBOX || consent.consentType === ConsentType.LOA_UPLOAD || consent.consentType === ConsentType.DIGITAL_SIGNATURE)) {
        meterTypeForApi = 'NHH'; // For checkbox consent, we treat SMART meters as NHH
      }

      const address = meter.addressAsLine !== 'Address not available' ? meter.addressAsLine : undefined;
   
      return {
        fuelType: fuelTypeForApi,
        mpxn: meter.meterNo,
        type: meterTypeForApi,
        address
      };
    });
      
    // Initialize the consent object with dates
    let consentObj: any = {
      startDate,
      endDate,
      scope: consent.scope,
      accepted: true
    };

    if (relationshipType === RelationshipType.LANDLORD) {
      consentObj.scope = 'AGGREGATE';
    }

    let consentType = undefined;
    if (consent.consentType === ConsentType.MAC_ADDRESS) {
      consentType = 'MACID';
      consentObj.macId = consent.consentData as string // Assuming consentData is a MAC address string
      consentObj.moveInDate = formatDateForApi(consent.moveInDate!);
    } else if (consent.consentType === ConsentType.LOA_UPLOAD) {
      if (consent.consentData instanceof File) {
        formData.append('file', consent.consentData);
      } else {
        console.error('Invalid consent data type for LOA upload');
        throw new Error('Invalid consent data type for LOA upload');
      } 
    } else if (consent.consentType === ConsentType.DIGITAL_SIGNATURE) {
      if (typeof consent.consentData === 'string') {
        consentId = consent.consentData; // Use existing consent ID
      } else {
        console.error('Invalid consent data type for LOA upload');
        throw new Error('Invalid consent data type for LOA upload');
      }
    } else if (consent.consentType === ConsentType.STRIPE) {
      consentType = 'STRIPE';
      consentObj.moveInDate = formatDateForApi(consent.moveInDate!);
      // Handle Stripe payment method creation in the service layer
      // The consent.consentData should contain the Stripe payment element and credentials
      const stripeData = consent.consentData as any;
      if (stripeData?.stripe && stripeData.stripeElements && stripeData.stripeCredentials) {
        try {
          // Create Stripe payment method before API submission
          console.log('Creating Stripe payment method for consent');
          
          // Call createStripePaymentMethod to create the payment method
          const paymentResult = await this.createStripePaymentMethod(stripeData, meters[0]);
          
          if (!paymentResult.success) {
            console.error('Failed to create Stripe payment method:', paymentResult.error);
            throw new Error(paymentResult.error || 'Failed to create Stripe payment method');
          }
          
          console.log('Stripe payment method created successfully:', paymentResult.setupIntent);
          
          // Add the payment method to the consent object
          consentObj.stripeSetupIntentId = paymentResult.setupIntent?.id;
          consentObj.stripeSecret = (stripeData.stripeCredentials as StripeCredentials).stripeSecret;
        
        } catch (error) {
          console.error('Error processing Stripe payment:', error);
          // const errorMessage = error instanceof Error ? `Stripe Error:: ${error.message}` : 'Unable to process Stripe payment';
          const errorMessage = 'Unable to verify your card details. Please check if your card is active and the details entered are correct.';
          throw new Error(errorMessage);
        }
      } else {
        console.error('Invalid Stripe consent data');
        throw new Error('Invalid Stripe consent data');
      }
    } else if (consent.consentType === ConsentType.BILL_UPLOAD) {
      consentType = 'BILL_UPLOAD';
      if (consent.consentData instanceof File) {
        formData.append('file', consent.consentData);
        if (consent.moveInDate) {
          consentObj.moveInDate = formatDateForApi(consent.moveInDate);
        }
      } else {
        console.error('Invalid consent data type for bill upload');
        throw new Error('Invalid consent data type for bill upload');
      }
    } else if (consent.consentType === ConsentType.CHECKBOX) {
      // If consent is metadata object, merge with our basic consent object
      if (consent.consentData && typeof consent.consentData === 'object') {
        consentObj = {
          ...consent.consentData as object,
          ...consentObj
        };
      }
    }

    // Create the metadata object
    const metadata: any = {
      consentType,
      email,
      consentId, // Use existing consentId if available
      organisation,
      meters: metersForApi,
      relationshipType: mapRelationshipTypeToApi(relationshipType),
      consent: consentObj,
    };
    
    // Add organisation info if provided
    if (organisation) {
      metadata.organisation = organisation;
    }
    
    // Append metadata as a JSON string
    formData.append('metadata', JSON.stringify(metadata));
    
    // Log the request details for debugging
    console.log('Sending consent request:', {
      url: URL,
      metadata: metadata
    });
    
    // Make the actual API call and return the promise
    return this.http.post<any>(URL, formData, {
      params: {
        'skipExternalCalls': 'true'
      }
    }).pipe(
      tap(response => {
        console.log('Consent request response:', response);
      })
    ).toPromise();
  }

  private async createStripePaymentMethod(stripeData: {stripe: Stripe, stripeElements: StripeElements, stripeCredentials: StripeCredentials}, meter: MeterDetails): Promise<{ success: boolean; setupIntent?: SetupIntent; error?: string }> {
    try {
      let loginUser = this.loginService.loginUser;
      
      if (loginUser) {
        // Confirm setup intent to create payment method
        const { error, setupIntent } = await stripeData.stripe.confirmSetup({
          elements: stripeData.stripeElements,
          confirmParams: {
            return_url: '',
            payment_method_data: {
            billing_details: {
              name: loginUser.firstName + ' ' + loginUser.lastName,
              email: loginUser.email,
              address: {
                country: 'UK',
                line1: meter.addressAsLine,
                postal_code: meter.postCode,
              }
            }
          }
          },
          redirect: 'if_required'
        });

        if (error) {
          return { success: false, error: error.message };
        }
        
        return { success: true, setupIntent };
      } else {
        return { success: false, error: 'User not logged in' };
      }
    } catch (error) {
      console.error('Error in createStripePaymentMethod:', error);
      return { success: false, error: 'Card payment via Stripe failed' };
    }
  }
  
   /**
   * Check the status of a consent request
   * @param requestId The request ID returned from the consent request
   * @returns Observable with the consent status response
   */
  checkConsentStatus(requestId: string): Observable<any> {
    console.log(`Checking consent status for request ID: ${requestId}`);
    const URL = `${this.endPointURL}/v1/customer/consent-check`;
    
    // For production, use the HTTP POST request
    return this.http.post<any>(URL, { requestId });
  }
  
  /**
   * Poll the consent status API until we get an APPROVED status or reach max attempts
   * @param requestId The request ID from the initial consent request
   * @param maxAttempts Maximum number of polling attempts (default 10)
   * @param intervalMs Polling interval in milliseconds (default 3000)
   * @returns Observable that emits the final status response
   */
  pollConsentStatus(requestId: string, maxAttempts: number = 10, intervalMs: number = 3000): Observable<any> {
    return new Observable<any>(observer => {
      let attempts = 0;
      
      const pollFunc = () => {
        attempts++;
        
        this.checkConsentStatus(requestId).subscribe({
          next: (response) => {
            const loaStatus = response.message?.consentStatus;

            // Check if status is final (APPROVED or REJECTED)
            if (loaStatus === 'APPROVED' || loaStatus === 'REJECTED' || loaStatus === 'FAILED') {
              // Emit the result and complete
              observer.next({
                ...response,
                finalStatus: true,
                attempts: attempts,
                isTimeout: false
              });
              observer.complete();
            } else if (attempts >= maxAttempts) {
              // We've reached max attempts with AUDIT_INPROGRESS - this is a timeout
              observer.next({
                ...response,
                finalStatus: true,
                attempts: attempts,
                isTimeout: true
              });
              observer.complete();
            } else {
              // Status is still AUDIT_INPROGRESS and we haven't reached max attempts
              observer.next({
                ...response,
                finalStatus: false,
                attempts: attempts,
                isTimeout: false
              });
              
              // Continue polling after delay
              setTimeout(pollFunc, intervalMs);
            }
          },
          error: (error) => {
            observer.error(error);
          }
        });
      };
      
      // Start polling
      pollFunc();
    });
  }

  /**
   * Request a digital signature for consent
   * @param meterNo The meter number to request consent for
   * @returns Observable with accessLink and consentId
   */
  requestDigitalSignatureConsent(digitalSign: DigitalSignature): Observable<{ accessLink: string, consentId: number }> {
    return this.http.post<any>(`${this.baseUrlCarbon}/v3/consent`, digitalSign, {
      headers: {
        'e_api_key': environment.eApiKey,
      },
      params: new HttpParams().set('utmSource', 'PERSE')
    }).pipe(
      map(response => {
        if (response.status === 'Success' && response.data) {
          return {
            accessLink: response.data.accessLink,
            consentId: response.data.consentId
          };
        } else {
          throw new Error('Failed to create digital signature consent');
        }
      }),
      catchError(error => {
        console.error('Error requesting digital signature consent:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Check the status of a digital signature consent
   */
  checkDigitalSignatureStatus(consentId: string): Observable<{ consentId: string, consentStatus: string }> {
    const url = `${this.baseUrlCarbon}/v3/consent/${consentId}`;

    return this.http.get<any>(url, {
      headers: {
        'e_api_key': environment.eApiKey
      },
      params: new HttpParams().set('utmSource', 'PERSE')
    }).pipe(
      map(response => {
        return {
          consentId: consentId,
          consentStatus: response.data?.consentStatus || response.consentStatus || 'pending'
        };
      }),
      catchError(error => {
        console.error('Error checking digital signature status:', error);
        // Return a default response in case of error
        return of({
          consentId: consentId,
          consentStatus: 'error'
        });
      })
    );
  }

  /**
   * Withdraw consent for a meter
   * @param meterNumber - The meter number to withdraw consent for
   * @returns Observable<boolean> - Success status
   */
  withdrawConsent(meters: string[]): Observable<boolean> {
    const url = `${this.endPointURL}/v1/customer/withdraw-consent`;
    
    return this.loginService.loginUserEmail.pipe(
      switchMap(email => {
        if (!email) {
          console.error('User email not available. User may not be logged in.');
          return throwError(() => new Error('User not logged in'));
        }
        
        const requestBody = {
          email: email,
          mpxns: meters
        };

        console.log(`Withdrawing consent for meters: ${meters.join(', ')} for user: ${email}`);

        return this.http.post<any>(url, requestBody, {
          headers: {
            'Content-Type': 'application/json'
          }
        }).pipe(
          map(response => {
            if (response && (response.status === true || response.success === true)) {
              console.log(`Consent withdrawn successfully for meters: ${meters.join(', ')}`);
              // Clear cache since meters data has changed
              this.clearCache();
              return true;
            }
            throw new Error('Failed to withdraw consent');
          }),
          catchError(error => {
            console.error('Error withdrawing consent:', error);
            return throwError(() => error);
          })
        );
      })
    );
  }

  /**
   * Download consumption data for a meter as CSV file
   * @param meterNo - The meter number (MPAN) to download data for
   * @returns Observable<void> - Downloads the file directly
   */
  downloadConsumptionData(meterNo: string): Observable<void> {
    const { startDate: toDate, endDate: fromDate } = calculateConsentDateRange();
    
    // Format dates as YYYY-MM-DD
    const toDateStr = formatDateForApi(toDate);
    const fromDateStr = formatDateForApi(fromDate);
    
    const url = `${environment.baseUrl}/meterhistory/v1/realtime-data`;
    const params = new URLSearchParams({
      fromDate: fromDateStr,
      toDate: toDateStr,
      MPAN: meterNo
    });
    
    return this.http.get(`${url}?${params.toString()}`, {
      headers: {
        'Accept': 'text/csv'
      },
      responseType: 'blob'
    }).pipe(
      map(blob => {
        // Create a download link and trigger download
        const downloadURL = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadURL;
        link.download = `consumption-data-${meterNo}-${fromDateStr}-to-${toDateStr}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadURL);
      }),
      catchError(error => {
        console.error('Error downloading consumption data:', error);
        return throwError(() => error);
      })
    );
  }

  getSmartMeterTerms(meterNumber: string): Observable<string> {
    const url = `${environment.baseUrl}/core/v1/smart-meter-tnc?format=link`;

    return this.loginService.loginUserEmail.pipe(
      switchMap(email => {
        if (!email) {
          console.error('User email not available. User may not be logged in.');
          return throwError(() => new Error('User not logged in'));
        }
        
        const requestBody = {
          email: email,
          meterNumber: meterNumber
        };

        return this.http.post<any>(url, requestBody).pipe(
          map(response => {
            if (response.status && response.data) {
              return response.data;
            } else {
              throw new Error('Failed to get smart meter terms and conditions link');
            }
          }),
          catchError(error => {
            console.error('Error getting smart meter terms:', error);
            return throwError(() => error);
          })
        );
      })
    );
  }
}


